package pl.nickers.match3.ecs

/**
 * E5 — Void Puddles. Stamps [CursedComponent] on regular ingredients that come to
 * **rest** on a void puddle cell; pieces merely falling through leave no mark.
 *
 * "At rest" is detected via the phase machine's stability guarantees:
 *  - `RESOLVE / MATCH_DETECT`: gravity has run to stable and no earlier RESOLVE
 *    subphase did work this cycle (the dispatcher reroutes to GRAVITY otherwise),
 *    so nothing on the board can fall any lower. Registered **before**
 *    [MatchDetectionSystem], so a piece that lands on a puddle and simultaneously
 *    completes a match is cursed before that match is counted — no goal credit.
 *  - `INPUT`: idle board. Covers pieces seeded directly on a puddle and restored
 *    saves, which reach INPUT without passing through RESOLVE.
 *
 * The puddle terrain entity is never consumed. Communicates only via component
 * state (no calls into other systems); cursed pieces are excluded from goal
 * credit by `countPieceIds`.
 */
internal class CurseSystem(
    private val pieceSet: PieceSet = PieceSetLoader.default(),
) : BaseSystem() {
    private lateinit var posMapper: ComponentMapper<GridPositionComponent>
    private lateinit var cursedMapper: ComponentMapper<CursedComponent>

    override fun initialize() {
        posMapper = world.mapper()
        cursedMapper = world.mapper()
    }

    override fun processSystem() {
        val board = world.boardState() ?: return
        val boardStable = board.phase == GamePhase.INPUT ||
            (board.phase == GamePhase.RESOLVE && board.currentSubphase == ResolveSubphase.MATCH_DETECT)
        if (!boardStable) return
        val puddles = world.getEntitiesForAspect(
            Aspect.all(GridPositionComponent::class, VoidPuddleTerrainComponent::class),
        )
        if (puddles.isEmpty()) return
        val puddleCells = puddles.mapNotNullTo(mutableSetOf()) { id ->
            posMapper[id]?.let { it.row to it.col }
        }
        val pieces = world.getEntitiesForAspect(
            Aspect.all(GridPositionComponent::class, JellyTypeComponent::class),
        )
        for (id in pieces) {
            if (cursedMapper.has(id)) continue
            if (!world.isFamily(id, pieceSet, Family.JELLY)) continue
            val pos = posMapper[id] ?: continue
            if (pos.row to pos.col in puddleCells) cursedMapper.set(id, CursedComponent())
        }
    }
}
